import styled, { css } from 'styled-components';
import { Button } from '@/components/UI/Button/Button';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { ChevronDown } from '@/components/UI/Icons/Icons';

export const DropdownIcon = styled(ChevronDown)``;

const ActiveOrderItem = css`
  background-color: ${({ theme }) => theme.palette.secondary.background};
`;

const ActiveOrderButton = css`
  border-color: ${({ theme }) => theme.palette.brand.main};

  ${DropdownIcon} {
    transform: rotate(180deg);
  }

  & > svg,
  & > path {
    fill: ${({ theme }) => theme.palette.brand.main};

    @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
      fill: ${({ theme }) => theme.palette.primary.labels};
    }
  }
`;

const CategoryHover = css`
  &:hover {
    background-color: ${({ theme }) => theme.palette.secondary.border};
  }
`;

const MinimizedOrderButton = css`
  height: 40px;
  border: 0;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.palette.secondary.background};
`;

export const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 55px;
  width: 100%;
  margin-bottom: 20px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 40px;
    flex-direction: row-reverse;
    justify-content: flex-end;
  }
`;

export const CategoriesList = styled.div`
  position: relative;
  display: flex;
  gap: 10px;
  margin-right: 10px;
  align-items: center;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-right: 0;
    margin-left: 5px;
  }
`;

export const CategoryItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  width: fit-content;
  align-items: center;
  justify-content: center;
  padding: 11px 15px;
  font-size: 15px;
  cursor: pointer;
  text-wrap: nowrap;
  white-space: nowrap;
  overflow: visible;
  user-select: none;
  height: 40px;
  border-radius: 100px;
  transition: all 0.1s linear;
  pointer-events: ${({ $isActive }) => ($isActive ? 'none' : 'all')};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.palette.system.white : theme.palette.primary.labels};
  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.palette.brand.main : theme.palette.secondary.background};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.md}) {
    ${({ $isActive }) => !$isActive && CategoryHover}
  }
`;

export const OrderButton = styled(Button).attrs<{
  $isActive: boolean;
  $isMinimized: boolean;
}>({
  appearance: 'transparent',
})`
  position: relative;
  z-index: 3;
  height: 40px;
  display: flex;
  gap: 10px;
  border-radius: 100px;
  user-select: none;
  word-break: keep-all;
  white-space: nowrap;
  transition: border 0.1s linear;
  background-color: ${({ theme }) => theme.palette.secondary.background};

  & > svg,
  & > path {
    transition: fill 0.1s linear;
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.secondary.border};
  }

  ${({ $isActive }) => $isActive && ActiveOrderButton}
  ${({ $isMinimized }) => $isMinimized && MinimizedOrderButton};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 0;
  }
`;

export const ItemLoader = styled(SkeletonLoader)<{ $width: number }>`
  height: 40px;
  border-radius: 100px;
  min-width: ${({ $width }) => $width}px;
`;

export const OrderMenu = styled.div`
  display: flex;
  gap: 2px;
  flex-direction: column;
  width: 240px;
  padding: 10px;
`;

export const OrderMenuItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 15px;
  cursor: pointer;
  border-radius: 14px;
  transition: all 0.1s linear;

  &:hover {
    ${ActiveOrderItem};
  }

  ${({ $isActive }) => $isActive && ActiveOrderItem};
`;

export const OrderMenuItemText = styled(Typography.Body).attrs(() => ({
  $variant: '1',
}))``;

export const RadioButton = styled.div<{ $isActive: boolean }>`
  position: relative;
  width: 20px;
  height: 20px;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.palette.brand.main : theme.palette.secondary.border};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.system.white};

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 11px;
    height: 11px;
    border-radius: 50%;
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
    background-color: ${({ theme }) => theme.palette.brand.main};
    display: ${({ $isActive }) => ($isActive ? 'block' : 'none')};
  }
`;

export const SheetWrapper = styled.div``;

export const OrderList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const OrderListItem = styled.div`
  display: flex;
  height: 60px;
  padding: 0 15px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.palette.primary.border};
`;

export const ScrollerButton = styled.div<{
  $isReversed?: boolean;
  $rightOffset?: number;
}>`
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 35px;
  height: 35px;
  border-radius: 50%;
  cursor: pointer;
  user-select: none;
  box-shadow: 0px 6px 12px 0px #0000000d;
  ${({ $isReversed, $rightOffset }) =>
    $isReversed ? 'left: 0' : `right: ${$rightOffset ?? 0}px`};
  border: 1px solid ${({ theme }) => theme.palette.primary.border};
  background-color: ${({ theme }) => theme.palette.system.white};

  svg {
    transform: rotate(${({ $isReversed }) => ($isReversed ? '90' : '-90')}deg);
  }
`;
